// Affaires sur téléphone : liste par étape, vue et modification d'une affaire (spec § 11).
import { Pencil } from "lucide-react";
import {
  EditBase,
  Form,
  InfiniteListBase,
  ShowBase,
  useGetList,
  useNotify,
  useRedirect,
  useShowContext,
  useTranslate,
} from "ra-core";
import { lazy, Suspense } from "react";
import { Link } from "react-router";
import { ReferenceArrayField } from "@/components/admin/reference-array-field";
import { ReferenceField } from "@/components/admin/reference-field";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

import { ContactList } from "../deals/ContactList";
import { DealInputs } from "../deals/DealInputs";
import { findDealLabel } from "../deals/dealUtils";
import { FormToolbar } from "../layout/FormToolbar";
import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { MobileBackButton } from "../misc/MobileBackButton";
import { NoteCreate } from "../notes/NoteCreate";
import { NotesIterator } from "../notes/NotesIterator";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Deal, PartnerOrg } from "../types";
import { NextAction } from "./DealFollowUp";
import { InvitedBanner, InvitedDeals } from "./InvitedDeals";
import {
  DealTodo,
  HostChips,
  PartnerDealTabs,
  usePartnerDealTab,
  useTransmittedHosts,
} from "./PartnerDeals";
import { dealTabFilter } from "./partnerships";
import { useInvited, useInvitedState } from "./useInvited";
import { OrgPipeline, useDealPipeline, usePartnerOrg } from "./usePartnerOrg";
import { useListView } from "./projectMapView";
import { MapFallback, ViewToggle } from "./ViewToggle";

// Carte des affaires (MapLibre) chargée à la première ouverture.
const DealMap = lazy(() => import("./DealMap"));

/** Affaires en cours, groupées dans l'ordre des étapes ; dans une étape, l'action la plus proche d'abord. */
export const DealListMobile = () => {
  const translate = useTranslate();
  const { invited, isPending: offerPending } = useInvitedState();
  const { subscriber, org, isPending: orgPending } = usePartnerOrg();
  if (offerPending || orgPending) return null;
  // Radar : un partenaire invité n'a pas de pipeline, seulement la liste des affaires transmises.
  if (invited)
    return (
      <>
        <MobileHeader>
          <h1 className="flex-1 text-xl font-semibold">
            {translate("resources.deals.name", { smart_count: 2 })}
          </h1>
        </MobileHeader>
        <MobileContent>
          <InvitedDeals />
        </MobileContent>
      </>
    );
  // Radar : un abonné a le pipeline de son organisation, les affaires transmises et ses actions à faire.
  if (subscriber) return <PartnerDealMobile org={org} />;
  return <DealBoardMobile />;
};

const PartnerDealMobile = ({ org }: { org: PartnerOrg | undefined }) => {
  const translate = useTranslate();
  const [tab, setTab] = usePartnerDealTab();
  const { sale } = usePartnerOrg();
  const myOrgId = sale?.org_id ?? (org ? Number(org.id) : undefined);
  const hosts = useTransmittedHosts(myOrgId, tab === "transmitted");
  const tabs = (
    <div className="pb-4">
      <PartnerDealTabs tab={tab} onChange={setTab} />
      {tab === "transmitted" && (
        <HostChips
          chips={hosts.chips}
          host={hosts.host}
          onChange={hosts.setHost}
        />
      )}
    </div>
  );
  if (tab === "todo")
    return (
      <>
        <MobileHeader>
          <h1 className="flex-1 text-xl font-semibold">
            {translate("resources.deals.name", { smart_count: 2 })}
          </h1>
        </MobileHeader>
        <MobileContent>
          {tabs}
          <DealTodo org={org} />
        </MobileContent>
      </>
    );
  if (myOrgId == null) return null;
  if (tab === "transmitted" && hosts.isPending)
    return (
      <>
        <MobileHeader>
          <h1 className="flex-1 text-xl font-semibold">
            {translate("resources.deals.name", { smart_count: 2 })}
          </h1>
        </MobileHeader>
        <MobileContent>{tabs}</MobileContent>
      </>
    );
  const board = (
    <DealBoardMobile
      key={`${tab}-${hosts.host}`}
      top={tabs}
      filter={dealTabFilter(
        tab === "mine" ? "mine" : "transmitted",
        myOrgId,
        hosts.host,
      )}
    />
  );
  if (tab === "mine") return <OrgPipeline org={org}>{board}</OrgPipeline>;
  return hosts.hostOrg && !hosts.hostOrg.client_team ? (
    <OrgPipeline org={hosts.hostOrg}>{board}</OrgPipeline>
  ) : (
    board
  );
};

const DealBoardMobile = ({
  top,
  filter,
}: {
  top?: React.ReactNode;
  filter?: Record<string, unknown>;
} = {}) => {
  const translate = useTranslate();
  const { dealStages } = useConfigurationContext();
  const { data: deals, isPending } = useGetList<Deal>("deals", {
    filter: { "archived_at@is": null, ...filter },
    pagination: { page: 1, perPage: 500 },
    sort: { field: "next_action_date", order: "ASC" },
  });
  const [view, setView] = useListView("deals");

  return (
    <>
      <MobileHeader>
        <h1 className="flex-1 text-xl font-semibold">
          {translate("resources.deals.name", { smart_count: 2 })}
        </h1>
      </MobileHeader>
      <MobileContent>
        {top}
        {isPending ? null : !deals?.length ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            {translate("resources.deals.follow_up.empty_list")}
          </p>
        ) : view === "map" ? (
          <Suspense fallback={<MapFallback />}>
            <DealMap deals={deals} mobile />
          </Suspense>
        ) : (
          <div className="flex flex-col gap-6 pb-16">
            {dealStages.map((stage) => {
              const inStage = deals.filter((d) => d.stage === stage.value);
              if (!inStage.length) return null;
              return (
                <section key={stage.value} className="flex flex-col gap-2">
                  <h2 className="text-sm font-semibold text-muted-foreground">
                    {stage.label} · {inStage.length}
                  </h2>
                  {inStage.map((deal) => (
                    <DealRow key={deal.id} deal={deal} />
                  ))}
                </section>
              );
            })}
          </div>
        )}
        {!isPending && !!deals?.length && (
          // Bascule flottante au-dessus de la barre du bas, comme dans la liste des projets.
          <div className="fixed inset-x-0 bottom-20 z-20 flex justify-center pointer-events-none">
            <div className="pointer-events-auto">
              <ViewToggle
                view={view}
                onChange={setView}
                floating
                listLabel="resources.deals.map.view_list"
              />
            </div>
          </div>
        )}
      </MobileContent>
    </>
  );
};

const DealRow = ({ deal }: { deal: Deal }) => (
  <Link to={`/deals/${deal.id}/show`}>
    <Card className="py-3 active:bg-muted">
      <CardContent className="flex flex-col gap-1 px-4">
        <span className="font-medium leading-snug line-clamp-2">
          {deal.name}
        </span>
        <NextAction deal={deal} compact />
      </CardContent>
    </Card>
  </Link>
);

export const DealShowMobile = () => (
  <ShowBase>
    <DealShowMobileContent />
  </ShowBase>
);

const DealShowMobileContent = () => {
  const translate = useTranslate();
  const { dealCategories } = useConfigurationContext();
  const { record, isPending } = useShowContext<Deal>();
  const { dealStages } = useDealPipeline(record);
  const invited = useInvited();
  if (isPending || !record) return null;

  return (
    <>
      <MobileHeader>
        <MobileBackButton to="/deals" />
        <h1 className="flex-1 min-w-0 truncate text-base font-semibold">
          {record.name}
        </h1>
        {!invited && (
          <Button
            asChild
            variant="ghost"
            size="icon"
            className="rounded-full"
            aria-label={translate("ra.action.edit")}
          >
            <Link to={`/deals/${record.id}`}>
              <Pencil className="size-5" />
            </Link>
          </Button>
        )}
      </MobileHeader>
      <MobileContent>
        <div className="flex flex-col gap-4">
          <h2 className="text-xl font-semibold leading-snug">{record.name}</h2>
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary">
              {findDealLabel(dealStages, record.stage) ?? record.stage}
            </Badge>
            {record.category && (
              <Badge variant="outline">
                {dealCategories.find((c) => c.value === record.category)
                  ?.label ?? record.category}
              </Badge>
            )}
            {record.archived_at && (
              <Badge variant="outline">
                {translate("resources.deals.archived.title")}
              </Badge>
            )}
          </div>

          <Field label={translate("resources.deals.fields.next_action")}>
            <NextAction deal={record} />
          </Field>

          {invited && <InvitedBanner />}

          {record.company_id != null && (
            <Field label={translate("resources.deals.fields.company_id")}>
              <ReferenceField
                source="company_id"
                reference="companies"
                link="show"
                className="text-sm underline"
              />
            </Field>
          )}

          {record.project_id && (
            <Button variant="outline" asChild>
              <Link to={`/projects/${record.project_id}/show`}>
                {translate("resources.deals.follow_up.open_project")}
              </Link>
            </Button>
          )}

          {!!record.contact_ids?.length && (
            <Field label={translate("resources.deals.fields.contact_ids")}>
              <ReferenceArrayField
                source="contact_ids"
                reference="contacts_summary"
              >
                <ContactList />
              </ReferenceArrayField>
            </Field>
          )}

          {record.description && (
            <Field label={translate("resources.deals.fields.description")}>
              <p className="text-sm leading-6 whitespace-pre-line">
                {record.description}
              </p>
            </Field>
          )}

          <Separator />
          <InfiniteListBase
            resource="deal_notes"
            filter={{ deal_id: record.id }}
            sort={{ field: "date", order: "DESC" }}
            perPage={25}
            disableSyncWithLocation
            storeKey={false}
            empty={<NoteCreate reference="deals" />}
          >
            <NotesIterator reference="deals" />
          </InfiniteListBase>
        </div>
      </MobileContent>
    </>
  );
};

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs text-muted-foreground tracking-wide">{label}</span>
    {children}
  </div>
);

export const DealEditMobile = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  return (
    <EditBase
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: (data: Deal) => {
          notify("resources.deals.updated", {});
          redirect(`/deals/${data.id}/show`);
        },
      }}
    >
      <MobileHeader>
        <MobileBackButton to="/deals" />
        <h1 className="flex-1 text-base font-semibold">
          {translate("ra.action.edit")}
        </h1>
      </MobileHeader>
      <MobileContent>
        <Form>
          <DealInputs />
          <FormToolbar />
        </Form>
      </MobileContent>
    </EditBase>
  );
};
